import type { LscLiveDashboardRow } from '../types';

export type LiveMatchSource = {
  row: LscLiveDashboardRow;
  elapsedMinutes: number | null;
};

export type GroupedLiveGame = {
  competitionMatchId: string | null;
  sources: LiveMatchSource[];
  lead: LiveMatchSource;
};

function elapsedMinutes(startedAt: string | null | undefined, now: Date): number | null {
  if (!startedAt) {
    return null;
  }
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) {
    return null;
  }
  return Math.max(0, Math.floor((now.getTime() - started) / 60000));
}

function startTime(row: LscLiveDashboardRow): number {
  const parsed = Date.parse(row.stream_started_at ?? '');
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

function byEarliestStart(a: LscLiveDashboardRow, b: LscLiveDashboardRow): number {
  return startTime(a) - startTime(b);
}

function leadRow(rows: LscLiveDashboardRow[], streamingObligation: string | null | undefined): LscLiveDashboardRow {
  const ordered = [...rows].sort(byEarliestStart);
  if ((streamingObligation ?? '').trim().toLowerCase() === 'away') {
    const away = ordered.find((row) => (row.broadcaster_side ?? '').trim().toLowerCase() === 'away');
    if (away) {
      return away;
    }
  }
  return ordered[0];
}

export function groupLiveRowsByMatch(
  rows: LscLiveDashboardRow[],
  streamingObligation: string | null | undefined,
  now: Date = new Date()
): GroupedLiveGame[] {
  const groups: GroupedLiveGame[] = [];
  const byMatch = new Map<string, LscLiveDashboardRow[]>();

  for (const row of rows) {
    const matchId = (row.competition_match_id ?? '').trim();
    if (!matchId) {
      const source = { row, elapsedMinutes: elapsedMinutes(row.stream_started_at, now) };
      groups.push({ competitionMatchId: null, sources: [source], lead: source });
      continue;
    }
    const existing = byMatch.get(matchId);
    if (existing) {
      existing.push(row);
    } else {
      const bucket = [row];
      byMatch.set(matchId, bucket);
      groups.push({ competitionMatchId: matchId, sources: [], lead: { row, elapsedMinutes: null } });
    }
  }

  return groups.map((group) => {
    if (!group.competitionMatchId) {
      return group;
    }
    const bucket = byMatch.get(group.competitionMatchId) ?? [];
    const lead = leadRow(bucket, streamingObligation);
    const ordered = [lead, ...bucket.filter((row) => row !== lead)];
    const sources = ordered.map((row) => ({
      row,
      elapsedMinutes: elapsedMinutes(row.stream_started_at, now),
    }));
    return {
      competitionMatchId: group.competitionMatchId,
      sources,
      lead: sources[0],
    };
  });
}
